import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import "./TreeViewFullscreenControls.scss";
import { SearchSelect, type SearchSelectItem } from "../../../common/components/SearchSelect/SearchSelect";
import { TreeViewInternationalFilter } from "../TreeViewInternationalFilter/TreeViewInternationalFilter";
import defaultContent from "../../../content/treeView";
import type { TreeNodeData } from "../TreeViewDataLayer";
import { useIsMobile } from "../../ActionReports/hooks/useIsMobile";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";
import maskAboNumber from "../../../common/util/maskAboNumber";
import { useAppSelector } from "../../../store";
import type { LosUiHeaderFilterFlags } from "../../RwLos/LosToolbar/LosFilterModal/LosFilterModal";

interface TreeViewFullscreenControlsProps {
  allNodes: TreeNodeData[];
  inInternational: boolean;
  hideInternationalDownlines: boolean;
  hideVolume: boolean;
  hideZeroVolumeAbos: boolean;
  onApplyVolumeFilters: (hideVolume: boolean, hideZeroVolumeAbos: boolean) => void;
  enableInternationalToggle?: boolean;
  /** Market uiHeader filter flags forwarded to the international filter. */
  uiHeader?: LosUiHeaderFilterFlags;
  /** Freeform slot for report-specific controls (e.g. Who's New's month tabs),
   *  rendered below the search/international row. */
  extraControls?: ReactNode;
  expandPathToNode: (targetId: string) => void;
  panToNode: (targetId: string, shouldZoom?: boolean) => void;
  setSelectedAbo: (abo: TreeNodeData | null) => void;
  setSelectedNodeId: (id: string | null) => void;
}

export const TreeViewFullscreenControls = ({
  allNodes,
  inInternational,
  hideInternationalDownlines,
  hideVolume,
  hideZeroVolumeAbos,
  onApplyVolumeFilters,
  enableInternationalToggle = true,
  uiHeader,
  extraControls,
  expandPathToNode,
  panToNode,
  setSelectedAbo,
  setSelectedNodeId,
}: TreeViewFullscreenControlsProps) => {
  const { t } = useTranslation(["treeView"]);
  const isMaskAboNumber = useAppSelector((s) => s.boot.configuration.los.maskConfidentialAboNumber) ?? false;

  // Ref keeps the latest panToNode so the setTimeout callback below always
  // calls the version closing over post-expansion aboData, not a stale one
  // captured when selectionCallback was created — mirrors the same fix in
  // useTreeViewSearch.ts's handleExternalSearch, which needed it for the same reason.
  const panToNodeRef = useRef(panToNode);
  useEffect(() => {
    panToNodeRef.current = panToNode;
  }, [panToNode]);

  // controlsExtra hides its scrollbar (see .scss), so a mouse wheel's default
  // vertical scroll can't reach it — same fix as WhosNewActionReportLayout's
  // TreeViewControls for its non-fullscreen tabs row.
  const extraControlsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = extraControlsRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const nodesByAbo = useMemo(() => {
    const map = new Map<string, TreeNodeData>();
    allNodes.forEach((node) => map.set(node.abo.toString(), node));
    return map;
  }, [allNodes]);

  const searchCallback = useCallback(
    (searchText: string): SearchSelectItem[] => {
      const lowerSearch = searchText.toLowerCase();
      return allNodes
        .filter(
          (node) =>
            node.aboAttributes.aboName.toLowerCase().includes(lowerSearch) || node.abo.toString().includes(lowerSearch),
        )
        .slice(0, 20)
        .map((node) => {
          const aboLabel = isMaskAboNumber && node.isConfidential ? maskAboNumber(String(node.abo)) : node.abo;
          const sublabel = `${aboLabel} | ${getCountryNameFromCode(String(node.aboAttributes.isoCountry), storedLocale())}`;
          return {
            key: node.abo.toString(),
            label: node.aboAttributes.aboName,
            sublabel,
          };
        });
    },
    [allNodes, isMaskAboNumber],
  );

  const selectionCallback = useCallback(
    (key: string) => {
      const node = nodesByAbo.get(key);
      if (node) {
        expandPathToNode(key);
        setSelectedAbo(node);
        setSelectedNodeId(key);
        // Delay so the canvas redraws with expanded ancestors before panning;
        // panToNodeRef.current always resolves to the post-expansion closure.
        setTimeout(() => panToNodeRef.current(key, true), 50);
      }
      return { displayName: node?.aboAttributes.aboName ?? "" };
    },
    [nodesByAbo, expandPathToNode, setSelectedAbo, setSelectedNodeId],
  );

  const isMobile = useIsMobile();

  return (
    <div className="tree-view-fullscreen-header">
      <div className="tree-view-fullscreen-header__controls">
        <div className="tree-view-fullscreen-header__controlsRow">
          <div className="tree-view-fullscreen-header__controlsLeft">
            <div className="tree-view-fullscreen-header__controlsSearch">
              <SearchSelect
                placeholderText={t("searchLabel", defaultContent.searchLabel)}
                clearSearchAriaLabel={t("clearSearch", defaultContent.clearSearch)}
                searchCallback={searchCallback}
                selectionCallback={selectionCallback}
                searchText=""
                clear={() => {
                  setSelectedAbo(null);
                  setSelectedNodeId(null);
                }}
              />
            </div>
            {enableInternationalToggle && (
              <TreeViewInternationalFilter
                inInternational={inInternational}
                hideInternationalDownlines={hideInternationalDownlines}
                hideVolume={hideVolume}
                hideZeroVolumeAbos={hideZeroVolumeAbos}
                onApplyVolumeFilters={onApplyVolumeFilters}
                uiHeader={uiHeader}
              />
            )}
            {extraControls && !isMobile && (
              <div ref={extraControlsRef} className="tree-view-fullscreen-header__controlsExtra">
                {extraControls}
              </div>
            )}
          </div>
        </div>
        {extraControls && isMobile && (
          <div className="tree-view-fullscreen-header__controlsRow">
            <div ref={extraControlsRef} className="tree-view-fullscreen-header__controlsExtra">
              {extraControls}
            </div>
          </div>
        )}{" "}
      </div>
    </div>
  );
};
